/*
 * webmcp.js — WebMCP tool surface for AI agents.
 *
 * Exposes a few of the store's key actions (search, add to cart, view cart,
 * read the product on the current page) to browser-based AI agents via the
 * WebMCP API (https://webmachinelearning.github.io/webmcp/).
 *
 * WebMCP is an early draft that is still moving: the registration surface has
 * shifted from `navigator.modelContext.provideContext({tools})` (2025 / Chrome
 * EPP) to `document.modelContext.registerTool(tool)` (later drafts). This file
 * detects whichever surface exists and registers the same tool definitions
 * through it. In any browser without WebMCP it does nothing — no errors, no
 * side effects.
 *
 * Loaded as a classic (non-module) deferred script AFTER constants.js /
 * pubsub.js / global.js and after the inline `window.routes` block, so
 * `publish`, `fetchConfig` and `window.routes` are available. It still falls
 * back to Shopify's fixed default paths if any of those are missing, so it is
 * safe regardless of placement.
 */
(function () {
	"use strict";

	// ---- Feature detection: resolve a registration surface, or bail ----------
	var mc =
		(typeof navigator !== "undefined" && navigator.modelContext) ||
		(typeof document !== "undefined" && document.modelContext) ||
		null;
	if (!mc) return; // No WebMCP in this browser → no-op.

	// ---- Endpoints (prefer window.routes, fall back to Shopify defaults) ------
	var R = (typeof window !== "undefined" && window.routes) || {};
	var CART_ADD = R.cart_add_url || "/cart/add.js";
	var CART_URL = R.cart_url || "/cart";
	var SEARCH = R.predictive_search_url || "/search/suggest";

	// ---- Helpers -------------------------------------------------------------

	// Shopify money values from /cart.js and `product.variants | json` are
	// integers in CENTS. Format them using the shop money format when it uses a
	// simple {{amount}} token, otherwise fall back to a clean number so an agent
	// never receives a broken template string.
	function money(cents) {
		if (cents == null || isNaN(cents)) return "";
		var num = (cents / 100).toFixed(2);
		var fmt = (window.theme && window.theme.moneyFormat) || "";
		if (fmt && /\{\{\s*amount\s*\}\}/.test(fmt)) {
			return fmt.replace(/\{\{\s*amount\s*\}\}/, num);
		}
		return num;
	}

	// MCP tool result shape used by both WebMCP drafts.
	function text(t) {
		return { content: [{ type: "text", text: String(t) }] };
	}

	// POST JSON mirroring the theme's fetchConfig() header contract.
	function postJSON(url, body) {
		var cfg =
			typeof fetchConfig === "function"
				? fetchConfig("json")
				: {
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Accept: "application/json",
						},
				  };
		cfg.body = JSON.stringify(body);
		return fetch(url, cfg);
	}

	// Best-effort: nudge the theme's cart UI to refresh after an add.
	function publishCartUpdate(variantId) {
		try {
			// PUB_SUB_EVENTS is a lexical global (const in constants.js), not on
			// window; reference it by name with a guard and fall back to the
			// literal event string.
			var evt =
				(typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) ||
				"cart-update";
			if (typeof publish === "function") {
				publish(evt, { source: "webmcp", productVariantId: variantId });
			}
		} catch (e) {
			/* UI refresh is best-effort; never fail the tool for this. */
		}
	}

	// ---- Tool definitions (authored once, registered via either surface) -----
	var tools = [
		// 1) search_products ---------------------------------------------------
		{
			name: "search_products",
			description:
				"Search this store's catalog by keyword and return matching products " +
				"with title, price, availability, and product URL.",
			inputSchema: {
				type: "object",
				properties: {
					query: {
						type: "string",
						description: 'Keywords to search for, e.g. "black t-shirt".',
					},
					limit: {
						type: "integer",
						minimum: 1,
						maximum: 10,
						default: 5,
						description: "Max number of products to return (1-10).",
					},
				},
				required: ["query"],
			},
			annotations: { readOnlyHint: true },
			execute: async function (input) {
				try {
					var q = ((input && input.query) || "").trim();
					if (!q) return text("Please provide a search query.");
					var limit = Math.min(Math.max(parseInt(input.limit, 10) || 5, 1), 10);
					var url =
						SEARCH +
						".json?q=" +
						encodeURIComponent(q) +
						"&resources[type]=product&resources[limit]=" +
						limit +
						"&resources[options][unavailable_products]=last";
					var res = await fetch(url, { headers: { Accept: "application/json" } });
					if (!res.ok) return text("Search failed (HTTP " + res.status + ").");
					var data = await res.json();
					var products =
						(((data || {}).resources || {}).results || {}).products || [];
					if (!products.length) return text('No products found for "' + q + '".');
					var lines = products.map(function (p) {
						// Predictive-search `price` is a decimal STRING (major units),
						// not cents — display as-is.
						var price = p.price != null ? "" + p.price : "";
						var avail = p.available === false ? " [SOLD OUT]" : "";
						return (
							"- " + p.title + (price ? " — " + price : "") + avail +
							"\n  " + (p.url || "")
						);
					});
					return text(
						"Found " + products.length + ' product(s) for "' + q + '":\n' +
							lines.join("\n")
					);
				} catch (e) {
					return text("Search error: " + ((e && e.message) || e));
				}
			},
		},

		// 2) add_to_cart -------------------------------------------------------
		{
			name: "add_to_cart",
			description:
				"Add a product variant to the shopping cart by numeric variant ID. " +
				"Use get_product or search_products first to obtain a variant ID.",
			inputSchema: {
				type: "object",
				properties: {
					variantId: {
						type: ["integer", "string"],
						description:
							"Numeric variant ID (a product variant, not the product ID).",
					},
					quantity: {
						type: "integer",
						minimum: 1,
						default: 1,
						description: "How many to add (default 1).",
					},
				},
				required: ["variantId"],
			},
			execute: async function (input) {
				try {
					var id = input && input.variantId;
					if (id == null || ("" + id).trim() === "")
						return text("A variantId is required.");
					var qty = Math.max(parseInt(input.quantity, 10) || 1, 1);
					var res = await postJSON(CART_ADD, {
						items: [{ id: id, quantity: qty }],
					});
					var data = await res.json().catch(function () {
						return {};
					});

					// Shopify returns HTTP 422 + { status, message, description } on
					// errors (sold out, insufficient stock). Surface it to the agent.
					if (!res.ok || data.status) {
						var msg = data.description || data.message || "HTTP " + res.status;
						return text("Could not add to cart: " + msg);
					}

					publishCartUpdate(id); // best-effort UI refresh

					var added = (data.items && data.items[0]) || data;
					var addedTitle =
						added.product_title || added.title || "variant " + id;

					// Authoritative totals for the confirmation message.
					var summary = "";
					try {
						var cartRes = await fetch(CART_URL + ".js", {
							headers: { Accept: "application/json" },
						});
						if (cartRes.ok) {
							var cart = await cartRes.json();
							summary =
								" Cart now has " + cart.item_count + " item(s), total " +
								money(cart.total_price) + ".";
						}
					} catch (e) {
						/* summary is optional */
					}

					return text("Added " + qty + " × " + addedTitle + " to the cart." + summary);
				} catch (e) {
					return text("Add-to-cart error: " + ((e && e.message) || e));
				}
			},
		},

		// 3) view_cart ---------------------------------------------------------
		{
			name: "view_cart",
			description:
				"Return the current contents of the shopping cart: item count, each " +
				"line item (title, quantity, line price), and the cart total.",
			inputSchema: { type: "object", properties: {} },
			annotations: { readOnlyHint: true },
			execute: async function () {
				try {
					var res = await fetch(CART_URL + ".js", {
						headers: { Accept: "application/json" },
					});
					if (!res.ok)
						return text("Could not read cart (HTTP " + res.status + ").");
					var cart = await res.json();
					if (!cart.item_count) return text("The cart is empty.");
					var lines = (cart.items || []).map(function (it) {
						var name =
							it.product_title +
							(it.variant_title && it.variant_title !== "Default Title"
								? " (" + it.variant_title + ")"
								: "");
						var linePrice =
							it.final_line_price != null ? it.final_line_price : it.line_price;
						return "- " + name + " ×" + it.quantity + " — " + money(linePrice);
					});
					return text(
						"Cart has " + cart.item_count + " item(s):\n" +
							lines.join("\n") + "\nTotal: " + money(cart.total_price)
					);
				} catch (e) {
					return text("View-cart error: " + ((e && e.message) || e));
				}
			},
		},

		// 4) get_product -------------------------------------------------------
		{
			name: "get_product",
			description:
				"Return the variants of the product shown on the current page (id, " +
				"title/options, price, availability). Only works on a product page.",
			inputSchema: { type: "object", properties: {} },
			annotations: { readOnlyHint: true },
			execute: async function () {
				try {
					// The variant JSON is embedded by product-variant-picker.liquid
					// inside <variant-radios>/<variant-selects>.
					var host = document.querySelector(
						'variant-radios script[type="application/json"],' +
							' variant-selects script[type="application/json"]'
					);
					if (!host || !host.textContent.trim()) {
						return text("No product is available on the current page.");
					}
					var variants = JSON.parse(host.textContent);
					if (!Array.isArray(variants) || !variants.length) {
						return text("No product variants found on the current page.");
					}
					var title =
						(document.querySelector('meta[property="og:title"]') || {}).content ||
						document.title;
					var lines = variants.map(function (v) {
						// `product.variants | json` price is an integer in CENTS.
						return (
							"- id " + v.id + ": " +
							(v.title || (v.options || []).join(" / ")) +
							" — " + money(v.price) + (v.available ? "" : " [SOLD OUT]")
						);
					});
					return text(
						'Product "' + title + '" has ' + variants.length +
							" variant(s):\n" + lines.join("\n")
					);
				} catch (e) {
					return text("Get-product error: " + ((e && e.message) || e));
				}
			},
		},
	];

	// ---- Register through whichever surface exists (fail-safe) ---------------
	try {
		if (typeof mc.provideContext === "function") {
			mc.provideContext({ tools: tools }); // older / Chrome EPP shape
		} else if (typeof mc.registerTool === "function") {
			tools.forEach(function (t) {
				try {
					mc.registerTool(t); // current draft shape
				} catch (e) {
					/* skip a rejected tool, keep the rest */
				}
			});
		}
		// else: object present but neither method → unknown shape → no-op.
	} catch (e) {
		/* Any shape mismatch → silent no-op; must never break the page. */
	}
})();
